import { ActionMenu, ActionList, PageLayout } from '@primer/react';
import UprightLogo from '@upright/ui/common/generic/components/icons/logo/logo-platform-short.svg';
import { UprightLogoSymbolBlackWhite } from '@upright/ui/common/generic/components/icons/logo/upright-logo-symbol-black-white';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { Link } from '@upright/ui/common/generic/components/primer-custom/link';
import { breakpoints } from '@upright/ui/common/generic/global/breakpoints';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { Logout } from '@upright/ui/icons';
import { logout } from '@upright/ui/untidy/redux/actions/actions';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

type Variant = 'light' | 'dark';

interface Props {
  variant: Variant;
  /** Where the logo should link back to. Defaults to '/' for dark, '/dashboard' for light. */
  logoHref?: string;
}

/**
 * Minimal header used for `/chat`. Visually consistent with the surrounding
 * page chrome the user came from — `light` matches the white DMA dashboard
 * `TopHeader`, `dark` matches the platform-default black `BouToolHeader` —
 * but stripped down to just a logo (back to where they came from) and a
 * logout-only user menu.
 *
 * Intentionally avoids the heavier dependencies of either source header
 * (workspace dropdown, search, tools menu, own-insights disclaimer, tailoring
 * tutorials) since none of them apply on the chat page.
 *
 * The choice between light/dark is made in `HeaderMaybe` based on the
 * `?from=` entry-point hint with an org-type fallback when the hint is
 * missing.
 */
export function ChatHeader(props: Props) {
  const { variant, logoHref = variant === 'light' ? '/dashboard' : '/' } = props;
  const user = useGetLoggedInUser();
  const initials = getUserInitials(user?.firstName, user?.lastName, user?.username);
  return (
    <PageLayout.Header>
      <HeaderContent $variant={variant}>
        <LeftSection $variant={variant}>
          <LogoLink to={logoHref} indicator="override">
            <UprightLogo className="logo-full" style={{ width: '89px', height: '12.5px' }} />
            <UprightLogoSymbolBlackWhite className="logo-symbol" style={{ width: '27px', height: '16px' }} />
          </LogoLink>
        </LeftSection>
        <RightSection>
          <UserMenuWrapper>
            <ChatUserMenu initials={initials} variant={variant} />
          </UserMenuWrapper>
        </RightSection>
      </HeaderContent>
    </PageLayout.Header>
  );
}

function ChatUserMenu({ initials, variant }: { initials: string; variant: Variant }) {
  const dispatch = useDispatch();
  function handleLogout() {
    dispatch(logout());
  }
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <HoverWrapper $variant={variant}>
          <CustomAvatar>{initials}</CustomAvatar>
        </HoverWrapper>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={handleLogout}>
            <ActionList.LeadingVisual>
              <Logout width={20} height={20} />
            </ActionList.LeadingVisual>
            Logout
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

function getUserInitials(firstName?: string, lastName?: string, username?: string) {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
}

const lightVariantHeader = css`
  background-color: var(--bgColor-default);
  color: var(--fgColor-default);
  border-bottom: 1px solid var(--borderColor-default);
`;

const darkVariantHeader = css`
  background-color: ${BouColor.PLATFORM_BG_BLACK};
  color: ${BouColor.PLATFORM_TEXT_WHITE};
  border-bottom: none;
`;

const HeaderContent = styled.div<{ $variant: Variant }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  /* Right padding tightened to sm (8px) so the profile-icon's right margin
     visually matches its top margin. With the 32px avatar centered in a
     40px hover wrapper inside this 50px header (align-items: center), the
     icon already sits ~9px from the page top; the previous xl (24px)
     right padding made the corner feel lopsided. Left padding stays xl
     so the logo retains its breathing room. */
  padding: 0 var(--space__sm) 0 var(--space__xl);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  ${props => (props.$variant === 'light' ? lightVariantHeader : darkVariantHeader)}
`;

const LeftSection = styled.div<{ $variant: Variant }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: var(--space__sm);
  /* Drives the logo color via currentColor so dark mode reads as white. */
  color: ${props => (props.$variant === 'light' ? 'inherit' : BouColor.PLATFORM_ICON_WHITE)};

  .logo-full {
    display: block;
  }

  .logo-symbol {
    display: none;
  }

  @media (max-width: ${breakpoints[2]}) {
    .logo-full {
      display: none;
    }

    .logo-symbol {
      display: block;
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 40px;
  &:hover {
    color: inherit;
    background-color: transparent;
    text-decoration: none;
  }
`;

const UserMenuWrapper = styled.div`
  padding-left: var(--space__md);

  @media (max-width: ${breakpoints[0]}) {
    padding-left: 0;
  }
`;

const HoverWrapper = styled.div<{ $variant: Variant }>`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: ${props =>
      props.$variant === 'light' ? 'var(--bgColor-neutral-muted)' : 'rgba(255, 255, 255, 0.12)'};
    color: ${props => (props.$variant === 'light' ? 'var(--fgColor-default)' : BouColor.PLATFORM_TEXT_WHITE)} !important;
    text-decoration: none !important;
  }
`;

const CustomAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: var(--bgColor-accent-emphasis);
  color: var(--fgColor-onEmphasis);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;
